/**
 * Business Valuation Card
 * Displays latest opinion of value with valuation details
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Calculator, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, getValuationMethodName } from '../../utils/enhancedDashboardUtils';

const BusinessValuationCard = ({ latestValuation, formatDate }) => {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-indigo-600 to-violet-600 p-4 text-white">
        <div className="flex items-center space-x-3">
          <Calculator size={24} />
          <div>
            <h3 className="text-lg font-bold">Latest Range of Business Value</h3>
            <p className="text-xs text-indigo-100">Opinion of Value</p>
          </div>
        </div>
      </div>
      {latestValuation ? (
        <div className="p-4 space-y-1.5">
          {/* Business Value - Prominent Display */}
          <div className="text-center p-1.5 bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-200 rounded-lg">
            <p className="text-xl font-bold text-indigo-600">
              {formatCurrency(latestValuation.valuation_amount)}
            </p>
            <p className="text-xs text-gray-600">Median Business Value</p>
          </div>

          {/* Valuation Range - 2 columns */}
          <div className="grid grid-cols-2 gap-1.5 text-sm">
            <div className="text-center p-1 bg-indigo-50 rounded">
              <p className="text-sm font-bold text-indigo-600">
                {formatCurrency(latestValuation.valuation_low)}
              </p>
              <p className="text-xs text-gray-600">Low Range</p>
            </div>
            <div className="text-center p-1 bg-indigo-50 rounded">
              <p className="text-sm font-bold text-indigo-600">
                {formatCurrency(latestValuation.valuation_high)}
              </p>
              <p className="text-xs text-gray-600">High Range</p>
            </div>
          </div>

          {/* Financial Metrics - 2 columns */}
          {(latestValuation.revenue || latestValuation.ebitda) && (
            <div className="grid grid-cols-2 gap-1.5 text-sm">
              {latestValuation.revenue && (
                <div className="text-center p-1 bg-green-50 rounded">
                  <p className="text-sm font-bold text-green-600">
                    {formatCurrency(latestValuation.revenue)}
                  </p>
                  <p className="text-xs text-gray-600">Revenue</p>
                </div>
              )}
              {latestValuation.ebitda && (
                <div className="text-center p-1 bg-green-50 rounded">
                  <p className="text-sm font-bold text-green-600">
                    {formatCurrency(latestValuation.ebitda)}
                  </p>
                  <p className="text-xs text-gray-600">EBITDA</p>
                </div>
              )}
            </div>
          )}

          {/* Method and Type */}
          <div className="text-xs space-y-0.5 pt-1">
            <div className="flex justify-between">
              <span className="text-gray-600">Method:</span>
              <span className="font-semibold">{getValuationMethodName(latestValuation.method)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Type:</span>
              <span className="font-semibold capitalize">
                {latestValuation.valuation_type || latestValuation.type || 'Simple'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Date:</span>
              <span className="font-semibold">{formatDate(latestValuation.created_at)}</span>
            </div>
          </div>

          {/* Spacer */}
          <div className="h-2"></div>

          <button
            onClick={() => navigate('/valuation-dashboard')}
            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold transition text-sm flex items-center justify-center space-x-2 min-h-[44px]"
          >
            <span>View Details</span>
            <ChevronRight size={16} />
          </button>
        </div>
      ) : (
        <div className="text-center py-4 px-4">
          <p className="text-sm text-gray-500 mb-3">No valuation yet</p>
          <button
            onClick={() => navigate('/valuation')}
            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold transition text-sm min-h-[44px]"
          >
            Calculate Now
          </button>
        </div>
      )}
    </div>
  );
};

BusinessValuationCard.propTypes = {
  latestValuation: PropTypes.shape({
    valuation_amount: PropTypes.number,
    valuation_low: PropTypes.number,
    valuation_high: PropTypes.number,
    revenue: PropTypes.number,
    ebitda: PropTypes.number,
    method: PropTypes.string,
    valuation_type: PropTypes.string,
    type: PropTypes.string,
    created_at: PropTypes.string
  }),
  formatDate: PropTypes.func.isRequired
};

export default BusinessValuationCard;
